import React from 'react';
import { X } from 'lucide-react';

export interface MonatsFestsetzungAnzeige {
    arbeitstage?: number;
    wochenstunden?: number;
    lohnfaktor?: number;
    sollUmsatzMonat?: number;
    effektiverBruttolohn?: number;
    stdLohnGrund?: number;
    stdLohnEffektiv?: number;
    stdLohnGesamt?: number;
}

export interface FestsetzungHistorieEntry {
    monat: string;
    lockedAt?: string;
    lockedBy?: string;
    snapshot?: MonatsFestsetzungAnzeige;
}

interface FestsetzungsHistorieModalProps {
    isOpen: boolean;
    onClose: () => void;
    currencyCode: string;
    employeeName: string;
    hj1Entries: FestsetzungHistorieEntry[];
}

const formatCurrency = (value: number | undefined, currencyCode: string, decimalPlaces = 0): string => {
    const num = Number(value) || 0;
    return `${new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(num)} ${currencyCode}`;
};

const formatFactor = (value: number | undefined): string => {
    const num = Number(value) || 0;
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const renderMetricCell = (label: string, value: React.ReactNode, valueClass = 'text-pe-text') => (
    <div>
        <div className="text-stone-500 dark:text-slate-500 text-xs uppercase mb-1">{label}</div>
        <div className={`font-mono font-bold text-sm ${valueClass}`}>{value}</div>
    </div>
);

const renderEntry = (entry: FestsetzungHistorieEntry, currencyCode: string) => {
    const snap = entry.snapshot || {};

    return (
        <div
            key={entry.monat}
            className="border border-pe-border dark:border-slate-700 rounded-lg p-4 bg-pe-tile-header dark:bg-slate-800/50"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b border-pe-border dark:border-slate-700 pb-2">
                <span className="text-pe-text font-bold">{entry.monat}</span>
                {(entry.lockedAt || entry.lockedBy) && (
                    <div className="text-xs text-stone-500 dark:text-slate-500">
                        {entry.lockedAt && <span>{entry.lockedAt}</span>}
                        <span>{entry.lockedAt ? ' · ' : ''}{entry.lockedBy || 'Admin'}</span>
                    </div>
                )}
            </div>
            {entry.snapshot ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    {renderMetricCell('Arbeitstage', `${snap.arbeitstage ?? 0} Tage`)}
                    {renderMetricCell('Wochenstunden', `${snap.wochenstunden ?? 0} Std`)}
                    {renderMetricCell('Lohnfaktor', formatFactor(snap.lohnfaktor), 'text-amber-700 dark:text-amber-400')}
                    {renderMetricCell('Soll-Umsatz / Monat', formatCurrency(snap.sollUmsatzMonat, currencyCode), 'text-amber-700 dark:text-amber-400')}
                    {renderMetricCell('Effektiv Brutto', formatCurrency(snap.effektiverBruttolohn, currencyCode, 2), 'text-pe-accent-text')}
                    {renderMetricCell('Grundstundenlohn', formatCurrency(snap.stdLohnGrund, currencyCode, 2))}
                    {renderMetricCell('Effektivstundenlohn', formatCurrency(snap.stdLohnEffektiv, currencyCode, 2), 'text-emerald-700 dark:text-emerald-400')}
                    {renderMetricCell('Stundenlohn gesamt', formatCurrency(snap.stdLohnGesamt, currencyCode, 2), 'text-pe-text')}
                </div>
            ) : (
                <p className="text-stone-500 dark:text-slate-500 text-sm italic">Kein Monats-Snapshot vorhanden (Legacy-Daten).</p>
            )}
        </div>
    );
};

const FestsetzungsHistorieModal: React.FC<FestsetzungsHistorieModalProps> = ({
    isOpen,
    onClose,
    currencyCode,
    employeeName,
    hj1Entries,
}) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-stone-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-pe-card dark:bg-slate-900 border border-pe-border dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-pe-border dark:border-slate-700 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-pe-text tracking-wide">Festsetzungs-Historie</h2>
                        <p className="text-sm text-pe-muted dark:text-slate-400 mt-0.5">{employeeName}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-stone-500 dark:text-slate-400 hover:text-pe-text bg-pe-hover dark:bg-slate-800 hover:bg-stone-300 dark:hover:bg-slate-700 rounded-lg p-2 transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-6 py-5 space-y-3">
                    {hj1Entries.map((entry) => renderEntry(entry, currencyCode))}
                </div>
            </div>
        </div>
    );
};

export default FestsetzungsHistorieModal;
